import { useState, useRef } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, Lock, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

export default function ResetPasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState('form'); // form, loading, success, error
  const [message, setMessage] = useState('');
  const calledRef = useRef(false);

  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (calledRef.current) return;

    if (!token) {
      setStatus('error');
      setMessage('Token reset password tidak ditemukan.');
      return;
    }

    if (newPassword.length < 6) {
      setMessage('Password minimal 6 karakter.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage('Password dan konfirmasi tidak cocok.');
      return;
    }

    calledRef.current = true;
    setStatus('loading');
    setMessage('');

    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setStatus('success');
      setMessage(res.data.message || 'Password berhasil direset. Silakan login dengan password baru.');
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.detail || 'Token tidak valid atau sudah kadaluarsa.');
      calledRef.current = false;
    }
  };

  const goToLogin = () => {
    window.location.href = '/';
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800/80 backdrop-blur-lg border border-slate-700/50 rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-6">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Link Tidak Valid</h1>
          <p className="text-red-300 mb-6">Token reset password tidak ditemukan di URL.</p>
          <button onClick={goToLogin} data-testid="reset-go-to-login-btn" className="w-full py-3 px-6 bg-gradient-to-r from-teal-600 to-cyan-700 hover:from-teal-500 hover:to-cyan-600 text-white font-semibold rounded-xl transition-all duration-200">
            Ke Halaman Login
          </button>
        </motion.div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800/80 backdrop-blur-lg border border-slate-700/50 rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 10 }}
            className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-6"
          >
            <CheckCircle className="w-8 h-8 text-green-400" />
          </motion.div>
          <h1 className="text-2xl font-bold text-white mb-2">Password Berhasil Direset!</h1>
          <p className="text-green-300 mb-6">{message}</p>
          <button onClick={goToLogin} data-testid="reset-go-to-login-btn" className="w-full py-3 px-6 bg-gradient-to-r from-teal-600 to-cyan-700 hover:from-teal-500 hover:to-cyan-600 text-white font-semibold rounded-xl transition-all duration-200">
            Login dengan Password Baru
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-slate-800/80 backdrop-blur-lg border border-slate-700/50 rounded-2xl shadow-2xl p-8 max-w-md w-full"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto bg-teal-500/20 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-teal-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Reset Password</h1>
          <p className="text-slate-400 mt-1">Masukkan password baru Anda</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Password Baru</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Minimal 6 karakter"
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50"
                required
                minLength={6}
                data-testid="reset-new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Konfirmasi Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Ulangi password baru"
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50"
              required
              data-testid="reset-confirm-password"
            />
          </div>

          {message && status !== 'success' && (
            <p className="text-sm text-rose-400" data-testid="reset-error-message">{message}</p>
          )}

          <button
            type="submit"
            disabled={status === 'loading'}
            data-testid="reset-submit-btn"
            className="w-full py-3 px-6 bg-gradient-to-r from-teal-600 to-cyan-700 hover:from-teal-500 hover:to-cyan-600 text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Menyimpan...
              </>
            ) : (
              'Simpan Password Baru'
            )}
          </button>

          {status === 'error' && (
            <button
              type="button"
              onClick={goToLogin}
              className="w-full py-2.5 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Kembali ke Login
            </button>
          )}
        </form>
      </motion.div>
    </div>
  );
}
